import React, { useState, useEffect, useCallback } from "react";
import { CreateComment } from "./CreateComment";
import { projectURL } from "./utils/mockdata";
import ListSubComment from "./ListSubComment";

export const ListComment = ({ postId }) => {
    const [userComment, setUserComment] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [commentAdded, setCommentAdded] = useState(false);
    const [commentVisible, setCommentVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [length, setLength] = useState(0);

    
    const loggedUserId = sessionStorage.getItem("loggedUserId");

    const [dropdownOpen, setDropdownOpen] = useState(null);    
    
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editCommentId, setEditCommentId] = useState(null);
    const [editContent, setEditContent] = useState("");

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [deleteCommentId, setDeleteCommentId] = useState(null);


    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = () => setDropdownOpen(null);
        window.addEventListener("click", handleClickOutside);
        return () => window.removeEventListener("click", handleClickOutside);
    }, []);


    const handleEdit = (commentId) => {
        const comment = userComment.find((p) => p._id === commentId);
        setEditCommentId(commentId);
        setEditContent(comment?.comment || "");
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        try {
            const res = await fetch(`${projectURL}comments/${editCommentId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ comment: editContent }),
            });

            if (res.ok) {
                const updatedTime = new Date().toISOString();

                setUserComment((prev) =>
                    prev.map((p) =>
                        p._id === editCommentId ? { ...p, comment: editContent, updated_at: updatedTime } : p
                    )
                );
                setIsEditModalOpen(false);
                setEditCommentId(null);
                setEditContent("");
            }
        } catch (err) {
            console.error("Update error:", err);
        }
    };


    const handleDeleteClick = (commentId) => {
        setDeleteCommentId(commentId);
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        try {
        const res = await fetch(`${projectURL}comments/${deleteCommentId}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
        });

        if (res.ok) {
            setUserComment((prev) => prev.filter((p) => p._id !== deleteCommentId));
            setIsDeleteModalOpen(false);
            setDeleteCommentId(null);      
        }
        } catch (err) {
            // console.error("Delete error:", err);
        }
    };


    const fetchComments = useCallback(async (pageNumber = 1, reset = false) => {
        try {
            setLoading(true);
            const fetchCommentPath = `${projectURL}comments/${postId}?page=${pageNumber}&limit=5`; // adjust as needed
            const response = await fetch(fetchCommentPath, {
                headers: { "cors": "no-cors" }
            });
            const commentJson = await response.json();

            const newComments = commentJson?.comments || [];
            const isLastPage = newComments.length < 2;

            setUserComment(prev =>
                reset ? newComments : [...prev, ...newComments]
            );
            setHasMore(!isLastPage);
            setLength(commentJson?.total);
        } catch (error) {
            console.error("Failed to fetch comments:", error);
        } finally {
            setLoading(false);
        }
    }, [postId]);

    useEffect(() => {
        if (commentVisible) {
            fetchComments(1, true); // reset list when opening
            setPage(1);
        }
    }, [commentVisible, commentAdded, fetchComments]);

    const handleLoadMore = () => {
        const nextPage = page + 1;
        fetchComments(nextPage);
        setPage(nextPage);
    };

    function timeAgo(dateString) {
        const now = new Date();
        const date = new Date(dateString);
        const seconds = Math.floor((now - date) / 1000);
        const intervals = {
            year: 31536000,
            month: 2592000,
            week: 604800,
            day: 86400,
            hour: 3600,
            minute: 60,
            second: 1,
        };
        for (const [unit, value] of Object.entries(intervals)) {
            const count = Math.floor(seconds / value);
            if (count >= 1) return `${count} ${unit}${count > 1 ? 's' : ''} ago`;
        }
        return "just now";
    }

    return (
        <div>
            <CreateComment postId={postId} onCommentCreated={() => setCommentAdded(prev => !prev)} />

            <div>
                <span
                    className={`cursor-pointer ${commentVisible ? "text-red-500" : "text-blue-500"}`}
                    onClick={() => setCommentVisible(prev => !prev)}
                >
                    {commentVisible
                        ? `Hide Comments (${length > 0 ? length : '-'})`
                        : `Show Comments (${length > 0 ? length : '+'})`
                    }
                </span>
            </div>

            {commentVisible && userComment.map((data) => (
                <div className="flex justify-center items-center mt-3 shadow-md" key={data._id}>
                    <div className="relative flex flex-col bg-white shadow-sm border rounded-md w-full max-w-4xl border-1 border-dashed border-slate-600">

                        <div className="flex items-center justify-between p-1">
                            <div className="flex items-center">
                                <img
                                    alt="User Avatar"
                                    src={data?.created_by?.imagePath}
                                    className="relative inline-block h-8 w-8 rounded-full border border-blue-800"
                                />
                                <div className="flex flex-col ml-3 text-xs">
                                    <span className="font-bold text-blue-500">{data?.created_by?.username}</span>
                                    <span className="text-slate-400">{timeAgo(data?.created_at)}
                                        {data?.created_at === data?.updated_at ? "" : " | 🖊️edited"}
                                    </span>
                                </div>
                            </div>

                            {/* Dropdown */}
                            {data?.created_by?._id === loggedUserId && (
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                                <button
                                onClick={() =>
                                    setDropdownOpen((prev) => (prev === data._id ? null : data._id))
                                }
                                className="text-gray-600 hover:text-gray-800 px-2 py-1 rounded-full"
                                >
                                ⋮
                                </button>
                                {dropdownOpen === data._id && (
                                <div className="absolute right-2 w-28 bg-white border border-gray-300 rounded shadow-md z-10">
                                    <button
                                    onClick={() => handleEdit(data._id)}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                                    >
                                    Edit
                                    </button>
                                    <button
                                    onClick={() => handleDeleteClick(data._id)}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 text-red-500"
                                    >
                                    Delete
                                    </button>
                                </div>
                                )}
                            </div>
                            )}
                        </div>

                        <hr />
                        <div className="p-2">
                            <div className="text-slate-700 text-sm font-normal whitespace-pre-line text-base">{data?.comment}</div>
                        </div>

                        <ListSubComment commentId={data?._id} />
                    </div>
                </div>
            ))}

            {commentVisible && hasMore && (
                <div className="text-center mt-2">
                    <span className="text-blue-800"
                        onClick={handleLoadMore}
                        style={{ cursor: loading ? "not-allowed" : "pointer" }}
                    >
                        {loading ? "Loading comments ..." : "Show more comments"}
                    </span>
                </div>
            )}


            {/* Edit Modal */}
            {isEditModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-4 rounded-lg w-full max-w-md">
                    <h3 className="text-lg font-semibold mb-2">Edit Comment</h3>
                    <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={3}
                    className="w-full p-1 border border-gray-200 rounded"
                    />
                    <div className="flex justify-end mt-2 gap-2">
                    <button
                        onClick={() => setIsEditModalOpen(false)}
                        className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm"
                    >Cancel</button>
                    <button
                        onClick={handleSaveEdit}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                    >Save</button>
                    </div>
                </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="bg-white p-4 rounded-lg w-full max-w-md">
                    <h3 className="text-lg font-semibold mb-2">Are you sure you want to delete this comment?</h3>
                    <div className="flex justify-end mt-2 gap-2">
                    <button
                        onClick={() => setIsDeleteModalOpen(false)} // Close modal without deleting
                        className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400 text-sm"
                    >Cancel</button>
                    <button
                        onClick={handleDelete} // Proceed with deletion
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                    >Delete</button>
                    </div>
                </div>
                </div>
            )}
        </div>
    );
};
